import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../../../nurse-dashboard/theme";
import { getCancellationRules, saveCancellationRule } from "../../api/cancellationRulesApi";

const EMPTY_FORM = { role: "", canCancelWithinHours: 24, requiresSupervisorApproval: false };

// Admin UI for G13 Cancellation Rules — role is unique, so saving with an
// existing role's name updates that rule (upsert), matching the backend's
// POST /cancellation_rules/ semantics.
export default function CancellationRulesAdmin() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRules(await getCancellationRules());
    } catch (err) {
      console.error("Failed to load cancellation rules:", err);
      toast.error("Failed to load cancellation rules.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (rule) => {
    setForm({
      role: rule.role,
      canCancelWithinHours: rule.can_cancel_within_hours,
      requiresSupervisorApproval: rule.requires_supervisor_approval,
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.role.trim()) {
      toast.error("Role is required.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await saveCancellationRule({
        role: form.role.trim(),
        canCancelWithinHours: Number(form.canCancelWithinHours) || 0,
        requiresSupervisorApproval: form.requiresSupervisorApproval,
      });
      if (data?.status === "Success") {
        toast.success(data.message || "Cancellation rule saved.");
        setFormOpen(false);
        setForm(EMPTY_FORM);
        load();
      } else {
        toast.error(data?.message || "Could not save the rule.");
      }
    } catch (err) {
      console.error("Save cancellation rule failed:", err);
      toast.error(err?.response?.data?.message || "Could not save the rule.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ bgcolor: NURSE_COLORS.pageBg, minHeight: "100vh", p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" color={NURSE_COLORS.textPrimary}>
          Cancellation Rules
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => {
              setForm(EMPTY_FORM);
              setFormOpen(true);
            }}
          >
            New / Update Rule
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}
            sx={{ color: NURSE_COLORS.primary, border: `1px solid ${NURSE_COLORS.border}`, borderRadius: 20, textTransform: "none" }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
              <TableCell>Role</TableCell>
              <TableCell>Cancel Window</TableCell>
              <TableCell>Supervisor Approval</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: NURSE_COLORS.textSecondary }}>
                  No cancellation rules configured
                </TableCell>
              </TableRow>
            )}
            {rules.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.role}</TableCell>
                <TableCell>
                  {r.can_cancel_within_hours > 0 ? `${r.can_cancel_within_hours}h` : "Never"}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={r.requires_supervisor_approval ? "Required" : "Not required"}
                    color={r.requires_supervisor_approval ? "warning" : "default"}
                  />
                </TableCell>
                <TableCell>
                  <Chip size="small" label={r.is_active ? "Active" : "Inactive"} color={r.is_active ? "success" : "default"} />
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => openEdit(r)}>
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Cancellation Rule</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            label="Role"
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            sx={{ mt: 1, mb: 1.5 }}
            helperText="Matched case-insensitively (with substring fallback) against the caller's role."
          />
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Can cancel within (hours, 0 = never)"
            value={form.canCancelWithinHours}
            onChange={(e) => setForm((f) => ({ ...f, canCancelWithinHours: e.target.value }))}
            sx={{ mb: 1.5 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.requiresSupervisorApproval}
                onChange={(e) =>
                  setForm((f) => ({ ...f, requiresSupervisorApproval: e.target.checked }))
                }
              />
            }
            label="Requires supervisor approval"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={submitting}>
            {submitting ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
